import LockIcon from "@mui/icons-material/Lock";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Loader } from "components/loaders";
import LoadingError from "components/validation/LoadingError";
import { getAuth, multiFactor, TotpMultiFactorGenerator } from "firebase/auth";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { loginHook } from "utils/slide/link/accounts";
import AuthCard from "views/Auth/AuthCard";

export default function MfaRegister(props) {
  const { setRedirect } = props;

  const theme = useTheme();
  const auth = getAuth();
  const appName = "Pay & Connect";

  const [loading, setLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [totpSecret, setTotpSecret] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    var user = auth.currentUser;

    multiFactor(user)
      .getSession()
      .then((multiFactorSession) => {
        return TotpMultiFactorGenerator.generateSecret(multiFactorSession);
      })
      .then((totpSecret) => {
        setTotpSecret(totpSecret);
        setLoading(false);
      })
      .catch(() => {
        setLoadingSubmit(false);
        enqueueSnackbar("Unable to load QR code. Please refresh.", {
          variant: "error",
        });
      });
    // eslint-disable-next-line
  }, [auth]);

  const codeLength = totpSecret?.codeLength ?? 6;

  const handleChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, codeLength);
    setVerificationCode(digitsOnly);
  };

  const submitCode = async () => {
    setLoadingSubmit(true);
    var user = auth.currentUser;
    const multiFactorAssertion =
      TotpMultiFactorGenerator.assertionForEnrollment(
        totpSecret,
        verificationCode,
      );

    // Complete enrollment.
    multiFactor(user)
      .enroll(multiFactorAssertion, "mfa_totp")
      .then(() => {
        setLoadingSubmit(false);
        enqueueSnackbar("Device successfully registered for MFA", {
          variant: "success",
        });
        loginHook();
        setRedirect(true);
      })
      .catch(() => {
        setLoadingSubmit(false);
        enqueueSnackbar("Invalid confirmation code. Please try again.", {
          variant: "error",
        });
      });
  };

  return (
    <AuthCard
      header={<LockIcon sx={{ fontSize: "90px", color: "common.white" }} />}
    >
      <Typography variant="h4" sx={{ mb: 2 }}>
        Set up MFA
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        MFA is required for all Dashboard users.
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        To enable MFA, please use your authenticator app (such as Authy or
        Google Authenticator) to scan the QR code, or enter the code below
        manually.
      </Typography>
      <Typography variant="body2">
        Please then enter the 6-digit confirmation displayed in your
        authenticator app before proceeding.
      </Typography>

      {loading && (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Loader />
        </Box>
      )}
      {!loading && !totpSecret && (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <LoadingError
            title={"Error"}
            text={"Error retrieving QR code. Please refresh and try again."}
          />
        </Box>
      )}
      {!loading && totpSecret && (
        <>
          <Box sx={{ p: 2, textAlign: "center" }}>
            <QRCode
              size={256}
              style={{ height: "auto", width: "50%" }}
              value={totpSecret.generateQrCodeUrl(
                auth.currentUser.email,
                appName,
              )}
              fgColor={theme.palette.primary.main}
            />
          </Box>
          <Typography variant="caption" align="center" sx={{ mb: 2 }}>
            {totpSecret.secretKey}
          </Typography>
          <TextField
            label="Confirmation code"
            value={verificationCode}
            onChange={handleChange}
            fullWidth
            autoFocus
            margin="normal"
            slotProps={{
              htmlInput: {
                inputMode: "numeric",
                pattern: "[0-9]*",
                maxLength: codeLength,
                autoComplete: "one-time-code",
                style: {
                  fontFamily: "monospace",
                  fontSize: "1.5rem",
                  letterSpacing: "0.5em",
                  textAlign: "center",
                },
              },
            }}
          />
          <Box sx={{ pt: 2 }}>
            <Button
              loading={loadingSubmit}
              onClick={submitCode}
              fullWidth
              variant="contained"
              disabled={verificationCode.length < codeLength}
            >
              Confirm
            </Button>
          </Box>
        </>
      )}
    </AuthCard>
  );
}
